<template>
  <div
    :id="'v-step-' + hash"
    :ref="'v-step-' + hash"
    :class="{ 'v-step--sticky': isSticky }"
    class="v-step"
  >
    <slot name="header">
      <div v-if="step.header" class="v-step__counter">
        <div v-if="step.header.title">
          {{ `${step.params.index}/${step.params.totalSteps}` }}
        </div>
      </div>
      <div v-if="step.header" class="v-step__header">
        <div v-if="step.header.title" v-html="step.header.title"></div>
      </div>
    </slot>

    <slot name="content">
      <div class="v-step__content">
        <div v-if="step.content" v-html="step.content"></div>
        <div v-else>
          This is a demo step! The id of this step is {{ hash }} and it targets
          {{ step.target }}.
        </div>
      </div>
    </slot>

    <slot name="actions">
      <div class="v-step__buttons">
        <button
          v-if="!isLast && isButtonEnabled('buttonSkip')"
          class="v-step__button v-step__button-skip"
          @click.prevent="skip"
        >
          {{ labels.buttonSkip }}
        </button>
        <div>
          <button
            v-if="!isFirst && isButtonEnabled('buttonPrevious')"
            class="v-step__button v-step__button-previous"
            @click.prevent="previousStep"
          >
            {{ labels.buttonPrevious }}
          </button>
          <button
            v-if="!isLast && isButtonEnabled('buttonNext')"
            class="v-step__button v-step__button-next"
            @click.prevent="nextStep"
          >
            {{ labels.buttonNext }}
          </button>
          <button
            v-if="isLast && isButtonEnabled('buttonStop')"
            class="v-step__button v-step__button-stop"
            @click.prevent="finish"
          >
            {{ labels.buttonStop }}
          </button>
        </div>
      </div>
    </slot>

    <div
      v-if="version === 'V1'"
      class="v-step__arrow"
      :class="{ 'v-step__arrow--dark': step.header && step.header.title }"
      data-popper-arrow
    ></div>
    <div
      v-if="version === 'V2'"
      class="v-step__arrow"
      :class="{ 'v-step__arrow--light': step.header && step.header.title }"
      data-popper-arrow
    ></div>
  </div>
</template>

<script>
import { createPopper } from '@popperjs/core';
import jump from 'jump.js';
import sum from 'hash-sum';
import { DEFAULT_STEP_OPTIONS, HIGHLIGHT } from './constants';
import { el } from 'date-fns/locale';

export default {
  name: 'VStep',
  props: {
    step: {
      type: Object,
    },
    previousStep: {
      type: Function,
    },
    nextStep: {
      type: Function,
    },
    stop: {
      type: Function,
    },
    skip: {
      type: Function,
      default: function () {
        this.stop();
      },
    },
    finish: {
      type: Function,
      default: function () {
        this.stop();
      },
    },
    isFirst: {
      type: Boolean,
    },
    isLast: {
      type: Boolean,
    },
    labels: {
      type: Object,
    },
    enabledButtons: {
      type: Object,
    },
    highlight: {
      type: Boolean,
    },
    stopOnFail: {
      type: Boolean,
    },
    debug: {
      type: Boolean,
    },
    version: {
      type: String,
      required: false,
    },
  },
  data() {
    return {
      hash: sum(this.step.target),
      targetElement: this.getElement(this.step.target),
    };
  },
  computed: {
    params() {
      return {
        ...DEFAULT_STEP_OPTIONS,
        ...{ highlight: this.highlight }, // Use global tour highlight setting first
        ...{ enabledButtons: Object.assign({}, this.enabledButtons) },
        ...this.step.params, // Then use local step parameters if defined
      };
    },
    /**
     * A step is considered sticky if it has no target.
     */
    isSticky() {
      return !this.step.target;
    },
  },
  mounted() {
    this.createStep();
  },
  destroyed() {
    this.removeHighlight();
  },
  methods: {
    getElement(target) {
      var importWizard = document.querySelector('w2p-import-wizard-wrapper');
      var importWizardOld =
        this.version === 'V1'
          ? document.querySelector('.w2p-import-wizard')
          : document.querySelector('.import-wizard-v2');

      if (importWizard && importWizard?.shadowRoot) {
        return importWizard?.shadowRoot.querySelector(target);
      } else if (importWizardOld && importWizardOld.shadowRoot) {
        return importWizardOld?.shadowRoot.querySelector(target);
      } else {
        return document.querySelector(target);
      }
    },
    createStep() {
      if (this.debug) {
        console.log(
          '[Vue Tour] The target element ' +
            this.step.target +
            ' of .v-step[id="' +
            this.hash +
            '"] is:',
          this.targetElement
        );
      }

      if (this.isSticky) {
        document.body.appendChild(this.$refs['v-step-' + this.hash]);
      } else {
        if (this.targetElement) {
          this.enableScrolling();
          this.createHighlight();

          createPopper(
            this.targetElement,
            this.$refs['v-step-' + this.hash],
            this.params
          );
        } else {
          if (this.debug) {
            console.error(
              '[Vue Tour] The target element ' +
                this.step.target +
                ' of .v-step[id="' +
                this.hash +
                '"] does not exist!'
            );
          }
          this.$emit('targetNotFound', this.step);
          if (this.stopOnFail) {
            this.stop();
          }
        }
      }
    },
    enableScrolling() {
      if (this.params.enableScrolling) {
        if (this.step.duration || this.step.offset) {
          let jumpOptions = {
            duration: this.step.duration || 1000,
            offset: this.step.offset || 0,
            callback: undefined,
            a11y: false,
          };

          jump(this.targetElement, jumpOptions);
        } else {
          // Use the native scroll by default if no scroll options has been defined
          this.targetElement.scrollIntoView({ behavior: 'smooth' });
        }
      }
    },
    isHighlightEnabled() {
      if (this.debug) {
        console.log(
          `[Vue Tour] Highlight is ${
            this.params.highlight ? 'enabled' : 'disabled'
          } for .v-step[id="${this.hash}"]`
        );
      }
      return this.params.highlight;
    },
    createHighlight() {
      if (this.isHighlightEnabled()) {
        document.body.classList.add(HIGHLIGHT.classes.active);
        const transitionValue = window
          .getComputedStyle(this.targetElement)
          .getPropertyValue('transition');

        // Make sure our background doesn't flick on transitions
        if (transitionValue !== 'all 0s ease 0s') {
          this.targetElement.style.transition = `${transitionValue}, ${HIGHLIGHT.transition}`;
        }

        this.targetElement.classList.add(HIGHLIGHT.classes.targetHighlighted);
        // The element must have a position, if it doesn't have one, add a relative position class
        if (!this.targetElement.style.position) {
          this.targetElement.classList.add(HIGHLIGHT.classes.targetRelative);
        }
      } else {
        document.body.classList.remove(HIGHLIGHT.classes.active);
      }
    },
    removeHighlight() {
      if (this.isHighlightEnabled()) {
        const target = this.targetElement;
        const currentTransition = this.targetElement.style.transition;
        this.targetElement.classList.remove(
          HIGHLIGHT.classes.targetHighlighted
        );
        this.targetElement.classList.remove(HIGHLIGHT.classes.targetRelative);
        // Remove our transition when step is finished.
        if (currentTransition.includes(HIGHLIGHT.transition)) {
          setTimeout(() => {
            target.style.transition = currentTransition.replace(
              `, ${HIGHLIGHT.transition}`,
              ''
            );
          }, 0);
        }
      }
    },
    isButtonEnabled(name) {
      return Object.prototype.hasOwnProperty.call(
        this.params.enabledButtons,
        name
      )
        ? this.params.enabledButtons[name]
        : true;
    },
  },
};
</script>

<style lang="scss" scoped>
.v-step {
  background: #393939; /* #ffc107, #35495e */
  color: white;
  max-width: 400px;
  border-radius: 3px;
  box-shadow: rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0) 0px 0px 0px 0px,
    rgba(0, 0, 0, 0.1) 0px 4px 6px -1px, rgba(0, 0, 0, 0.06) 0px 2px 4px -1px;
  padding: 15px;
  pointer-events: auto;
  text-align: center;
  z-index: 1000;

  &--sticky {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);

    & .v-step__arrow {
      display: none;
    }
  }
}

.v-step__arrow,
.v-step__arrow::before {
  position: absolute;
  width: 10px;
  height: 10px;
  background: inherit;
}

.v-step__arrow {
  visibility: hidden;

  &--dark {
    &:before {
      background: #393939;
    }
  }
}

.v-step__arrow::before {
  visibility: visible;
  content: '';
  transform: rotate(45deg);
  margin-left: -5px;
}

.v-step[data-popper-placement^='top'] > .v-step__arrow {
  bottom: -5px;
}

.v-step[data-popper-placement^='bottom'] > .v-step__arrow {
  top: -5px;
}

.v-step[data-popper-placement^='right'] > .v-step__arrow {
  left: -5px;
}

.v-step[data-popper-placement^='left'] > .v-step__arrow {
  right: -5px;
}

/* Custom */
.v-step__counter {
  border-top-left-radius: 3px;
  border-top-right-radius: 3px;
  text-align: left;
  font-family: Roboto;
  font-size: 12px;
  font-weight: normal;
  line-height: normal;
  letter-spacing: 0.14px;
  color: #c6c6c6;
}

.v-step__header {
  margin: 7px 0px 0px 0px;
  border-top-left-radius: 3px;
  border-top-right-radius: 3px;
  text-align: left;
  font-family: Roboto;
  font-size: 14px;
  font-weight: bold;
  line-height: 1.29;
  letter-spacing: 0.16px;
}

.v-step__content {
  margin: 7px 0px 0px 0px;
  text-align: left;
  font-family: Roboto;
  font-size: 14px;
  font-weight: normal;
  line-height: 1.29;
  letter-spacing: 0.16px;
}

.v-step__button {
  font-family: Roboto;
  font-size: 14px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.29;
  letter-spacing: 0.16px;
  color: white;
  background-color: #24a148;
  padding: 7px 18px 4px 16px;
  border: 0px;
  margin: 5px;

  &:hover {
    background-color: #229242;
    color: white;
    cursor: pointer;
  }
}

.v-step__buttons {
  display: flex;
  justify-content: space-between;
  text-align: right;
  margin: 14px 0px 0px 0px;
}

.v-step__button-skip {
  background: transparent;
  color: #42be65;
  padding: 0;

  &:hover {
    background-color: transparent;
    color: white;
    cursor: pointer;
    color: #42be65;
  }
}
</style>
